import { useState, useEffect } from "react";
import API from "../api";

export default function Budgeting() {

  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [form, setForm] = useState({
    id: null,
    category: "",
    amount: "",
    period: "Monthly"
  });

  /* ===============================
     FETCH
  =============================== */
  const fetchBudgets = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${API}/budgets`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();
      setBudgets(data.data || []);

    } catch (err) {
      console.error("❌ Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBudgets();
  }, []);

  /* ===============================
     CREATE / UPDATE
  =============================== */
  const handleSubmit = async () => {
    try {
      if (!form.category || !form.amount) {
        return alert("Fill all fields");
      }

      const token = localStorage.getItem("token");

      const url = editMode
        ? `${API}/budgets/${form.id}`
        : `${API}/budgets`;

      const method = editMode ? "PUT" : "POST";

      await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });

      fetchBudgets();
      closeModal();

    } catch (err) {
      console.error(err);
    }
  };

  /* ===============================
     DELETE
  =============================== */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete budget?")) return;

    const token = localStorage.getItem("token");

    await fetch(`${API}/budgets/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` }
    });

    setBudgets(prev => prev.filter(b => b.id !== id));
  };

  /* ===============================
     EDIT
  =============================== */
  const handleEdit = (b) => {
    setForm(b);
    setEditMode(true);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditMode(false);
    setForm({
      id: null,
      category: "",
      amount: "",
      period: "Monthly"
    });
  };

  /* ===============================
     KPI
  =============================== */
  const total = budgets.reduce((s,b)=>s+Number(b.amount || 0),0);
  const spent = budgets.reduce((s,b)=>s+Number(b.spent || 0),0);
  const remaining = total - spent;

  const format = (v) => "₱" + Number(v).toLocaleString();

  return (
    <div className="space-y-6">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold">Budgeting</h2>

        <button
          onClick={()=>setShowModal(true)}
          className="bg-green-600 text-white px-4 py-2 rounded-lg"
        >
          Add Budget
        </button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-3 gap-6">

        <div className="bg-white p-5 border rounded">
          <h4>Total Budget</h4>
          <p className="text-xl">{format(total)}</p>
        </div>

        <div className="bg-white p-5 border text-yellow-600 rounded">
          <h4>Spent</h4>
          <p className="text-xl">{format(spent)}</p>
        </div>

        <div className="bg-white p-5 border text-green-600 rounded">
          <h4>Remaining</h4>
          <p className="text-xl">{format(remaining)}</p>
        </div>

      </div>

      {/* TABLE */}
      <div className="bg-white border rounded-xl overflow-x-auto">

        {loading ? (
          <div className="p-6 text-center">Loading...</div>
        ) : budgets.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No budgets yet
          </div>
        ) : (
          <table className="w-full text-sm">

            <thead className="bg-gray-100">
              <tr>
                <th className="p-4">Category</th>
                <th>Budget</th>
                <th>Spent</th>
                <th>Progress</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {budgets.map((b) => {

                const percent = Math.min(
                  100,
                  (Number(b.spent || 0) / Number(b.amount || 1)) * 100
                );

                return (
                  <tr key={b.id} className="border-t hover:bg-gray-50">

                    <td className="p-4 font-medium">{b.category}</td>
                    <td>{format(b.amount)}</td>
                    <td>{format(b.spent)}</td>

                    {/* PROGRESS */}
                    <td className="w-48">
                      <div className="bg-gray-200 h-2 rounded">
                        <div
                          className="bg-green-600 h-2 rounded"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <small>{percent.toFixed(0)}%</small>
                    </td>

                    {/* ACTION */}
                    <td className="flex gap-2 p-4">
                      <button
                        onClick={()=>handleEdit(b)}
                        className="text-blue-600 text-sm"
                      >
                        Edit
                      </button>

                      <button
                        onClick={()=>handleDelete(b.id)}
                        className="text-red-600 text-sm"
                      >
                        Delete
                      </button>
                    </td>

                  </tr>
                );
              })}
            </tbody>

          </table>
        )}

      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center">

          <div className="bg-white p-6 rounded-xl w-96 space-y-3">

            <h3 className="font-bold text-lg">
              {editMode ? "Edit Budget" : "Add Budget"}
            </h3>

            <input
              placeholder="Category"
              className="w-full p-2 border"
              value={form.category}
              onChange={(e)=>setForm({...form,category:e.target.value})}
            />

            <input
              placeholder="Amount"
              type="number"
              className="w-full p-2 border"
              value={form.amount}
              onChange={(e)=>setForm({...form,amount:e.target.value})}
            />

            <select
              className="w-full p-2 border"
              value={form.period}
              onChange={(e)=>setForm({...form,period:e.target.value})}
            >
              <option>Monthly</option>
              <option>Weekly</option>
            </select>

            <div className="flex justify-end gap-2">
              <button onClick={closeModal}>Cancel</button>

              <button
                onClick={handleSubmit}
                className="bg-green-600 text-white px-4 py-2 rounded"
              >
                {editMode ? "Update" : "Save"}
              </button>
            </div>

          </div>

        </div>
      )}

    </div>
  );
}